import moment, { Moment } from 'moment';

/**
 * Date Format Types
 * Comprehensive list of supported date formatting options
 */
export type TDateFormat =
  | 'fullDate' // 'MMM D, YYYY' (e.g., "Mar 15, 2026")
  | 'fullDatetime' // 'MMM D, YYYY [at] h:mma' (e.g., "Mar 15, 2026 at 2:30pm")
  | 'monthDay' // 'MM-D' (e.g., "03-15")
  | 'monthYear' // 'MMMM YYYY' (e.g., "March 2026")
  | 'shortDate' // 'MM/DD/YY' (e.g., "03/15/26")
  | 'time' // 'h:mma' (e.g., "2:30pm")
  | 'year'; // 'YYYY' (e.g., "2026")

/**
 * Mapping of format types to moment format strings
 */
const FORMAT_MAP: Record<TDateFormat, string> = {
  fullDate: 'MMM D, YYYY',
  fullDatetime: 'MMM D, YYYY [at] h:mma',
  monthDay: 'MM-D',
  monthYear: 'MMMM YYYY',
  shortDate: 'MM/DD/YY',
  time: 'h:mma',
  year: 'YYYY',
};

/**
 * Converts a date input to a valid string for moment parsing.
 *
 * IMPORTANT: Safari has limitations with ISO date strings (particularly those with Z or timezone info).
 * This function ensures the date is properly formatted as a string that moment can reliably parse.
 * If a Date object is passed, it is converted to ISO string. If a string is passed, it is returned as-is.
 *
 * @param date - The date to convert (Date object or string)
 * @returns A string representation of the date suitable for moment parsing
 *
 * @example
 * // Date object
 * const result = convertDateToString(new Date('2026-03-15'));
 * // Returns: "2026-03-15T14:30:00.000Z" (or similar ISO format)
 *
 * @example
 * // String
 * const result = convertDateToString('2026-03-15T14:30:00Z');
 * // Returns: "2026-03-15T14:30:00Z"
 */
const convertDateToString = (date: Date | string): string => {
  if (date instanceof Date) {
    // Convert Date to ISO string for consistent parsing
    return date.toISOString();
  }
  // Ensure the string is fully a string (Safari fix)
  return String(date);
};

/**
 * Creates a moment object from a date input with proper validation.
 *
 * This function handles both Date objects and strings, converting them appropriately
 * and ensuring that moment receives valid input that Safari can parse correctly.
 *
 * @param date - The date to parse (Date object or string)
 * @returns A moment object or invalid moment if parsing fails
 *
 * @example
 * const m = parseToMoment(new Date());
 * const m = parseToMoment('2026-03-15');
 */
const parseToMoment = (date: Date | string | undefined | null): Moment => {
  if (!date) {
    return moment.invalid();
  }

  const dateString = convertDateToString(date);
  return moment(dateString);
};

/**
 * Formats a date with a specified format type. Handles both Date objects and string inputs.
 * 
 *  - `'fullDate'`: "Mar 15, 2026"
 *  - `'fullDatetime'`: "Mar 15, 2026 at 2:30pm"
 *  - `'monthDay'`: "03-15"
 *  - `'monthYear'`: "March 2026"
 *  - `'shortDate'`: "03/15/26"
 *  - `'time'`: "2:30pm"
 *  - `'year'`: "2026"
 *
 * @example
 * formatDate(new Date('2026-03-15T14:30:00Z'), 'fullDatetime');
 * // Returns: "Mar 15, 2026 at 2:30pm"
 *
 * @example
 * formatDate('2026-03-15', 'shortDate');
 * // Returns: "03/15/26"
 *
 * @example
 * formatDate(null, 'fullDate', 'N/A');
 * // Returns: "N/A"
 */
export const formatDate = (
  date: Date | string | undefined | null,
  format: TDateFormat,
  fallback: string = 'Invalid Date',
): string => {
  const m = parseToMoment(date);

  if (!m.isValid()) {
    return fallback;
  }

  const formatString = FORMAT_MAP[format];
  return m.format(formatString);
};

/**
 * Formats a date as a relative time string (e.g., "2 days ago").
 *
 * This is useful for displaying "time since" information in a human-readable format.
 * Handles both Date objects and string inputs with proper Safari compatibility.
 *
 * @param date - The date to format as relative time (Date object or string)
 * @param fallback - Optional fallback value if date is invalid (default: 'Unknown')
 * @returns The relative time string, or fallback if date is invalid
 *
 * @example
 * formatDateRelative(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000));
 * // Returns: "2 days ago"
 *
 * @example
 * formatDateRelative('2026-03-10');
 * // Returns: "5 days from now" (assuming today is 2026-03-15)
 *
 * @example
 * formatDateRelative(null, 'Never');
 * // Returns: "Never"
 */
export const formatDateRelative = (
  date: Date | string | undefined | null,
  fallback: string = 'Unknown',
): string => {
  const m = parseToMoment(date);

  if (!m.isValid()) {
    return fallback;
  }

  return m.fromNow();
};

/**
 * Gets the current year as a string.
 *
 * Useful for copyright notices, date comparisons, or other year-based logic.
 * Uses moment's formatting for consistency.
 *
 * @returns The current year in YYYY format (e.g., "2026")
 *
 * @example
 * const year = getCurrentYear();
 * // Returns: "2026"
 */
export const getCurrentYear = (): string => {
  return moment().format('YYYY');
};

/**
 * Validates if a date string or Date object is valid.
 *
 * Useful for pre-validation before passing dates to other functions.
 *
 * @param date - The date to validate (Date object or string)
 * @returns true if the date is valid, false otherwise
 *
 * @example
 * isValidDate('2026-03-15');
 * // Returns: true
 *
 * @example
 * isValidDate('invalid-date');
 * // Returns: false
 */
export const isValidDate = (date: Date | string | undefined | null): boolean => {
  if (!date) {
    return false;
  }

  const m = parseToMoment(date);
  return m.isValid();
};

/**
 * Formats a date with a custom moment format string.
 *
 * Use this when you need a format not covered by the predefined DateFormat types.
 * Be aware of Safari limitations with date string parsing.
 *
 * @param date - The date to format (Date object or string)
 * @param customFormat - The moment format string (e.g., 'YYYY-MM-DD', 'dddd, MMMM D')
 * @param fallback - Optional fallback value if date is invalid (default: 'Invalid Date')
 * @returns The formatted date string, or fallback if date is invalid
 *
 * @example
 * formatDateCustom(new Date(), 'dddd, MMMM D, YYYY');
 * // Returns: "Tuesday, March 15, 2026"
 *
 * @example
 * formatDateCustom('2026-03-15', 'YYYY-MM-DD HH:mm:ss');
 * // Returns: "2026-03-15 14:30:00"
 */
export const formatDateCustom = (
  date: Date | string | undefined | null,
  customFormat: string,
  fallback: string = 'Invalid Date',
): string => {
  const m = parseToMoment(date);

  if (!m.isValid()) {
    return fallback;
  }

  return m.format(customFormat);
};

/**
 * Formats a scorecard date string or Date object into a human-readable print-friendly format.
 *
 * Combines date and time with locale-specific formatting (en-US) suitable for printing.
 * Returns an empty string if the input date is missing or invalid.
 *
 * @param createdDate - ISO 8601 date string, Date object, or equivalent (e.g., from API timestamp)
 * @returns Formatted string like "May 5, 2026 at 3:45 PM", or empty string if date is missing
 *
 * @example
 * // With valid ISO string
 * formatScorecardPrintDate('2026-05-05T15:45:00Z')
 * // Returns: "May 5, 2026 at 3:45 PM"
 *
 * // With Date object
 * formatScorecardPrintDate(new Date('2026-05-05T15:45:00Z'))
 * // Returns: "May 5, 2026 at 3:45 PM"
 *
 * // With missing date
 * formatScorecardPrintDate(undefined)
 * // Returns: ""
 */
export const formatScorecardPrintDate = (createdDate?: string | Date | null): string => {
  // Falsy check: null, undefined, empty string all result in empty return
  if (!createdDate) return '';

  // Format the date portion: "May 5, 2026"
  const date = new Date(createdDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Format the time portion: "3:45 PM"
  const time = new Date(createdDate).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Combine into human-readable string: "May 5, 2026 at 3:45 PM"
  return `${date} at ${time}`;
};

/**
 * Exports all utility functions as a namespace for alternative import style.
 *
 * @example
 * import { dateUtils } from '@/utils/format-date';
 * dateUtils.formatDate(new Date(), 'fullDate');
 */
export const dateUtils = {
  convertDateToString,
  formatDate,
  formatDateCustom,
  formatDateRelative,
  formatScorecardPrintDate,
  getCurrentYear,
  isValidDate,
  parseToMoment,
};
